<template>
  <div class="order-details-page">
    <div class="container py-5">
      <!-- Breadcrumb / Back Link -->
      <div class="mb-4">
        <router-link :to="{ name: 'Account', query: { tab: 'orders' } }" class="back-link d-inline-flex align-items-center gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
          {{ t('account.orders.back_to_orders') }}
        </router-link>
      </div>

      <div v-if="isLoading" class="text-center py-5">
        <div class="spinner-border text-dark" role="status"></div>
        <p class="mt-3 small text-muted">{{ t('tracking.loading') }}</p>
      </div>

      <div v-else-if="error" class="text-center py-5">
        <p class="text-danger">{{ error }}</p>
        <button @click="fetchOrder" class="btn btn-dark mt-3">{{ t('account.orders.retry') || 'RIPROVA' }}</button>
      </div>

      <div v-else-if="order" class="order-content">
        <div class="row g-4">
          <!-- Colonna Sinistra: Timeline, Info Ordine, Prodotti, Note, Indirizzi -->
          <div class="col-lg-8">
            <!-- Order Header + Timeline -->
            <div class="card-premium p-4 mb-4">
              <div class="d-flex justify-content-between align-items-start mb-4">
                <div>
                  <h1 class="order-title mb-1">{{ t('account.orders.order_num') }} {{ order.orderNumber }}</h1>
                  <div class="d-flex flex-wrap gap-3">
                    <p class="text-muted small mb-0">{{ t('account.orders.order_date') }} {{ formatDate(order.orderDate) }}</p>
                    <p class="text-muted small mb-0">{{ t('account.orders.order_id') }}: {{ order.salesOrderId }}</p>
                    <p v-if="order.customerId" class="text-muted small mb-0">{{ t('account.orders.customer_id') }}: {{ order.customerId }}</p>
                    <p class="text-muted small mb-0">{{ order.totalQuantity || 0 }} {{ order.totalQuantity === 1 ? 'articolo' : 'articoli' }}</p>
                  </div>
                </div>
                <span class="status-badge" :class="(order.orderStatus || order.status || '').toLowerCase()">
                  {{ formatStatus(order.orderStatus || order.status) }}
                </span>
              </div>

              <!-- Timeline -->
              <div class="timeline-section">
                <h5 class="section-title mb-4">{{ t('account.orders.timeline_title') }}</h5>
                <div class="steps">
                  <div class="step" :class="getStepClass(0)">
                    <div class="bar"></div>
                    <div class="dot"></div>
                    <div class="label">{{ t('tracking.timeline.confirmed') }}</div>
                    <div class="date">{{ getStepDate(0) }}</div>
                  </div>
                  <div class="step" :class="getStepClass(1)">
                    <div class="bar"></div>
                    <div class="dot"></div>
                    <div class="label">{{ t('tracking.timeline.preparing') }}</div>
                    <div class="date">{{ getStepDate(1) }}</div>
                  </div>
                  <div class="step" :class="getStepClass(2)">
                    <div class="bar"></div>
                    <div class="dot"></div>
                    <div class="label">{{ t('tracking.timeline.shipped') }}</div>
                    <div class="date">{{ getStepDate(2) }}</div>
                  </div>
                  <div class="step" :class="getStepClass(3)">
                    <div class="bar"></div>
                    <div class="dot"></div>
                    <div class="label">{{ t('tracking.timeline.delivered') }}</div>
                    <div class="date">{{ getStepDate(3) }}</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Items -->
            <div class="card-premium p-4 mb-4">
              <h5 class="section-title mb-4">{{ t('account.orders.section_products') }}</h5>
              <div class="items-list">
                <div v-for="item in order.items" :key="item.orderItemId" class="order-product-item py-3 border-bottom">
                  <div class="row align-items-center g-3">
                    <div v-if="item.productImageUrl" class="col-2">
                      <img :src="item.productImageUrl.startsWith('http') ? item.productImageUrl : '/images/products/' + item.productImageUrl" class="img-fluid rounded-1" alt="Prodotto">
                    </div>
                    <div v-else class="col-2">
                      <div class="thumb-placeholder-box d-flex align-items-center justify-content-center">
                        <span class="thumb-placeholder-text">{{ (item.productName || 'P')[0] }}</span>
                      </div>
                    </div>
                    <div :class="item.productImageUrl ? 'col-6' : 'col-8'">
                      <h4 class="product-name mb-1">{{ item.productName }}</h4>
                      <p class="product-meta mb-0">
                        {{ t('product.labels.size') }}: {{ item.size }} | {{ t('product.labels.color') }}: {{ item.color || item.colorName }} | {{ t('cart.qty') }}: {{ item.quantity }}
                      </p>
                    </div>
                    <div class="col-2 text-end">
                      <span class="product-price fw-bold">{{ formatPrice(item.lineTotal) }}</span>
                      <p class="small text-muted mb-0">{{ formatPrice(item.unitPrice) }} / pz</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Note Cliente -->
            <div v-if="order.customerNote" class="card-premium p-4 mb-4">
              <h5 class="section-title mb-3">{{ t('account.orders.section_note') }}</h5>
              <p class="small mb-0 text-muted italic">"{{ order.customerNote }}"</p>
            </div>

            <!-- Indirizzi -->
            <div class="row g-4">
              <div class="col-md-6">
                <div class="card-premium p-4 h-100">
                  <h5 class="section-title mb-3">{{ t('tracking.shipping_address') }}</h5>
                  <div v-if="order.shippingAddress" class="small text-muted">
                    <p v-if="order.shippingAddress.firstName || order.shippingAddress.lastName" class="mb-1 fw-bold">
                      {{ order.shippingAddress.firstName }} {{ order.shippingAddress.lastName }}
                    </p>
                    <p v-if="order.shippingAddress.companyName" class="mb-1">{{ order.shippingAddress.companyName }}</p>
                    <p class="mb-1">{{ order.shippingAddress.addressLine1 || order.shippingAddress.street }}</p>
                    <p class="mb-0">
                      {{ order.shippingAddress.zipCode || '' }} {{ order.shippingAddress.city }}
                      <span v-if="order.shippingAddress.stateProvince && order.shippingAddress.stateProvince.trim() !== ''">({{ order.shippingAddress.stateProvince }})</span>
                      ({{ order.shippingAddress.country }})
                    </p>
                  </div>
                  <div v-else class="small text-muted italic">{{ t('account.orders.no_address') || 'Non fornito' }}</div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="card-premium p-4 h-100">
                   <h5 class="section-title mb-3">{{ t('checkout.billing_address') }}</h5>
                  <div v-if="order.billingAddress" class="small text-muted">
                    <p v-if="order.billingAddress.firstName || order.billingAddress.lastName" class="mb-1 fw-bold">
                      {{ order.billingAddress.firstName }} {{ order.billingAddress.lastName }}
                    </p>
                    <p v-if="order.billingAddress.companyName" class="mb-1">{{ order.billingAddress.companyName }}</p>
                    <p class="mb-1">{{ order.billingAddress.addressLine1 || order.billingAddress.street }}</p>
                    <p class="mb-0">
                      {{ order.billingAddress.zipCode || '' }} {{ order.billingAddress.city }}
                      <span v-if="order.billingAddress.stateProvince && order.billingAddress.stateProvince.trim() !== ''">({{ order.billingAddress.stateProvince }})</span>
                      ({{ order.billingAddress.country }})
                    </p>
                  </div>
                  <div v-else class="small text-muted italic">{{ t('account.orders.same_as_shipping') || 'Come spedizione' }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Colonna Destra: Riepilogo, Pagamento, Logistica -->
          <div class="col-lg-4">
            <!-- Riepilogo Finanziario -->
            <div class="card-premium p-4 mb-4 bg-light">
              <h5 class="section-title mb-4">{{ t('account.orders.section_summary') }}</h5>
              <div class="d-flex justify-content-between mb-2">
                <span class="text-muted small">{{ t('checkout.subtotal').toUpperCase() }}</span>
                <span class="small">{{ formatPrice(order.subtotal) }}</span>
              </div>
              <div class="d-flex justify-content-between mb-2">
                <span class="text-muted small">{{ t('checkout.shipping').toUpperCase() }}</span>
                <span class="small">{{ order.shippingCost > 0 ? formatPrice(order.shippingCost) : t('header.cart.free').toUpperCase() }}</span>
              </div>
              <div v-if="order.discountTotal > 0" class="d-flex justify-content-between mb-2 text-success">
                <span class="small">{{ t('header.cart.total_discount').toUpperCase() }} <span v-if="order.couponId" class="badge bg-success ms-1">{{ order.couponId }}</span></span>
                <span class="small">-{{ formatPrice(order.discountTotal) }}</span>
              </div>
              <hr>
              <div class="d-flex justify-content-between align-items-end">
                <span class="fw-bold">{{ t('checkout.total').toUpperCase() }}</span>
                <div class="text-end">
                  <div class="fw-bold fs-5">{{ formatPrice(order.orderTotal || order.totalAmount) }}</div>
                  <div class="price-vat-label-details">{{ t('product.labels.vat_included') }}</div>
                </div>
              </div>

              <div v-if="(order.orderStatus || order.status || '').toLowerCase() === 'pendingpayment'" class="mt-4">
                <button @click="goToPayment" class="btn btn-dark w-100 py-3 fw-bold letter-spacing-1">
                  {{ t('account.orders.pay_now') }}
                </button>
              </div>
            </div>

            <!-- Pagamenti -->
            <div v-if="order.payments && order.payments.length > 0" class="card-premium p-4 mb-4">
              <h5 class="section-title mb-3">{{ t('account.orders.section_payment') }}</h5>
              <div v-for="payment in order.payments" :key="payment.paymentId" class="py-2 border-bottom last-child-border-0">
                <div class="d-flex justify-content-between align-items-center">
                  <div class="d-flex align-items-center gap-2">
                    <img v-if="getPaymentMethodIcon(payment)" :src="getPaymentMethodIcon(payment)" :alt="getPaymentTypeLabel(payment)" class="payment-method-icon">
                    <span v-else class="small fw-bold text-uppercase">{{ payment.paymentMethod }}</span>
                    <span v-if="getPaymentMethodIcon(payment)" class="small fw-bold text-uppercase">{{ getPaymentTypeLabel(payment) }}</span>
                    <span v-if="payment.cardLast4" class="small text-muted">•••• {{ payment.cardLast4 }}</span>
                  </div>
                  <span class="small fw-bold">{{ formatPrice(payment.amount) }}</span>
                </div>
                <div class="d-flex justify-content-between align-items-center mt-1">
                  <span class="text-muted" style="font-size: 9px;">STATO: {{ payment.paymentStatus }}</span>
                  <span class="text-muted" style="font-size: 9px;">VALUTA: {{ payment.currency }}</span>
                </div>
              </div>
            </div>

            <!-- Info Spedizione -->
            <div class="card-premium p-4 mb-4">
              <h5 class="section-title mb-4">{{ t('account.orders.section_logistics') }}</h5>
              <div v-if="shipments.length > 0">
                <div v-for="(shipment, idx) in shipments" :key="idx" class="mb-3 last-child-mb-0">
                  <div class="d-flex align-items-center gap-2 mb-2">
                    <div class="status-dot" :class="(shipment.shipmentStatus || shipment.status || '').toLowerCase()"></div>
                    <span class="small fw-bold text-uppercase">{{ shipment.shipmentStatus || shipment.status }}</span>
                  </div>
                  <p class="small text-muted mb-1">Corriere: {{ shipment.carrier || 'In attesa' }}</p>
                  <p v-if="shipment.trackingCode" class="small text-muted mb-1">
                    Codice tracking:
                    <a
                      v-if="shipment.trackingCode"
                      :href="'https://www.google.com/search?q=' + shipment.trackingCode"
                      target="_blank"
                      class="fw-bold text-dark text-decoration-underline"
                    >
                      {{ shipment.trackingCode }}
                    </a>
                  </p>
                  <p v-if="shipment.estimatedDeliveryDate || shipment.estimatedArrivalDate" class="small text-muted mb-1">
                    Consegna stimata: {{ formatDate(shipment.estimatedDeliveryDate || shipment.estimatedArrivalDate) }}
                  </p>
                  <p v-if="shipment.shippedDate || shipment.shippedOn" class="small text-muted mb-0">
                    Spedito il: {{ formatDate(shipment.shippedDate || shipment.shippedOn) }}
                  </p>

                  <!-- Pulsante Traccia Spedizione -->
                  <a
                    v-if="getTrackingUrl(shipment.trackingUrl || shipment.trackingLink)"
                    class="btn btn-dark btn-sm w-100 mt-2"
                    :href="getTrackingUrl(shipment.trackingUrl || shipment.trackingLink)"
                    target="_blank"
                    rel="noopener"
                  >
                    {{ t('tracking.track_shipment') }}
                  </a>
                  <div v-else-if="!shipment.trackingCode" class="text-muted small mt-2">
                    {{ t('account.orders.no_tracking_available') }}
                  </div>
                </div>
              </div>
              <div v-else class="text-muted small">
                {{ t('account.orders.order_waiting') }}
              </div>

              <!-- Pulsante Richiedi Cambio / Reso -->
              <div v-if="canRequestReturnOrExchange" class="mt-4 pt-3 border-top">
                <a
                  :href="buildReturnMailto()"
                  class="btn btn-outline-dark btn-sm w-100 d-flex align-items-center justify-content-center gap-2"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                  </svg>
                  {{ t('account.orders.return_exchange') }}
                </a>
              </div>
            </div>
          </div>
        </div>

        <!-- Help Section -->
        <div class="help-section card-premium p-4 mt-4">
          <div class="text-center">
            <h5 class="section-title mb-3">{{ t('account.orders.help_title') }}</h5>
            <p class="small text-muted mb-4" style="max-width: 500px; margin: 0 auto; line-height: 1.6;">
              {{ t('account.orders.help_copy') }}
            </p>
            <a :href="`mailto:${supportEmail}?subject=Richiesta%20cambio%20/%20reso%20-%20Ordine%20${order.orderNumber}`" class="btn btn-dark btn-sm">
              {{ t('account.orders.need_help_cta') }}
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { salesOrderService } from '@/services/salesOrderService'
import { useLanguage } from '@/composables/useLanguage'

const visaIcon = new URL('../assets/icons/payments/visa-svgrepo-com.svg', import.meta.url).href
const mastercardIcon = new URL('../assets/icons/payments/mastercard-full-svgrepo-com.svg', import.meta.url).href
const googlePayIcon = new URL('../assets/icons/payments/google-pay-primary-logo-logo-svgrepo-com.svg', import.meta.url).href
const applePayIcon = new URL('../assets/icons/payments/apple-pay-logo-svgrepo-com.svg', import.meta.url).href
const paypalIcon = new URL('../assets/icons/payments/paypal-svgrepo-com.svg', import.meta.url).href
const linkIcon = new URL('../assets/icons/payments/link-svgrepo-com.svg', import.meta.url).href
const klarnaIcon = new URL('../assets/icons/payments/klarna-svgrepo-com.svg', import.meta.url).href

const route = useRoute()
const router = useRouter()
const { t } = useLanguage()

const order = ref(null)
const isLoading = ref(true)
const error = ref(null)
const supportEmail = import.meta.env.VITE_SUPPORT_EMAIL

const formatPrice = (price) => {
  if (price === null || price === undefined) return '€0,00'
  const val = Number(price)
  if (!Number.isFinite(val)) return '€0,00'
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR'
  }).format(val)
}

const formatDate = (dateString) => {
  if (!dateString) return '-'
  const d = new Date(dateString)
  if (isNaN(d.getTime())) return '-'
  return new Intl.DateTimeFormat('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(d)
}

const formatTimelineDate = (dateStr) => {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  if (isNaN(d.getTime())) return ''
  return new Intl.DateTimeFormat('it-IT', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  }).format(d)
}

const formatStatus = (status) => {
  if (!status) return ''
  const statuses = {
    'pendingpayment': t('tracking.order_status.pendingpayment') || 'In attesa di pagamento',
    'pending': t('tracking.order_status.pendingpayment') || 'In attesa di pagamento',
    'paid': t('tracking.order_status.paid') || 'Pagato',
    'processing': t('tracking.order_status.processing') || 'In elaborazione',
    'paymentfailed': t('tracking.order_status.paymentfailed') || 'Pagamento fallito',
    'shipped': t('tracking.order_status.shipped') || 'Spedito',
    'delivered': t('tracking.order_status.delivered') || 'Consegnato',
    'cancelled': t('tracking.order_status.cancelled') || 'Annullato',
    'completed': t('tracking.order_status.completed') || 'Completato'
  }
  const statusKey = String(status).toLowerCase()
  return statuses[statusKey] || status
}

const getPaymentTypeLabel = (payment) => {
  if (!payment) return '-'
  const rawMethod = String(payment.paymentMethod || '').toLowerCase()
  const rawDetail = String(payment.paymentMethodDetail || '').toLowerCase()

  if (rawMethod.includes('googlepay') || rawDetail.includes('googlepay')) return 'Google Pay'
  if (rawMethod.includes('applepay') || rawDetail.includes('applepay')) return 'Apple Pay'
  if (rawMethod.includes('paypal')) return 'PayPal'
  if (rawMethod.includes('link')) return 'Link'
  if (rawMethod.includes('klarna')) return 'Klarna'
  if (rawMethod === 'card' || rawMethod === '' || rawDetail === 'card') {
    if (payment.cardBrand) return 'Carta ' + payment.cardBrand.charAt(0).toUpperCase() + payment.cardBrand.slice(1)
    return 'Carta'
  }

  return payment.paymentMethod || '-'
}

const getPaymentMethodIcon = (payment) => {
  const method = String(payment.paymentMethod || '').toLowerCase()
  const detail = String(payment.paymentMethodDetail || '').toLowerCase()

  if (method.includes('googlepay') || detail.includes('googlepay')) return googlePayIcon
  if (method.includes('applepay') || detail.includes('applepay')) return applePayIcon
  if (method.includes('paypal') || detail.includes('paypal')) return paypalIcon
  if (method.includes('link') || detail.includes('link')) return linkIcon
  if (method.includes('klarna')) return klarnaIcon

  const brand = String(payment.cardBrand || '').toLowerCase()
  if (brand.includes('visa')) return visaIcon
  if (brand.includes('master') || brand.includes('mc')) return mastercardIcon
  return null
}

const isMoreThan31MinAgo = (dateString) => {
  if (!dateString) return false
  const d = new Date(dateString)
  if (isNaN(d.getTime())) return false
  return Date.now() - d.getTime() > 31 * 60 * 1000
}

const getStepClass = (stepIndex) => {
  const status = String(order.value?.orderStatus || order.value?.status || '').toLowerCase()
  const shipment = firstShipment.value || {}
  const shipStatus = String(shipment.shipmentStatus || shipment.status || '').toLowerCase()
  const orderDate = order.value?.orderDate

  const isProcessing = ['processing', 'shipped', 'intransit', 'outfordelivery', 'readyforpickup', 'delivered', 'completed'].includes(status)
  const isPaid = status === 'paid' || isProcessing
  const isOver31Min = isMoreThan31MinAgo(orderDate)
  const isShipped = ['shipping', 'shipped', 'intransit', 'outfordelivery', 'readyforpickup'].includes(shipStatus)
  const isDelivered = shipStatus === 'delivered' || status === 'delivered' || status === 'completed'

  if (stepIndex === 0) {
    return isPaid ? 'done' : 'pending'
  }
  if (stepIndex === 1) {
    if (isOver31Min || isProcessing || isShipped || isDelivered) return 'done'
    return 'pending'
  }
  if (stepIndex === 2) {
    if (isDelivered) return 'done'
    if (isShipped) return 'current'
    return 'pending'
  }
  if (stepIndex === 3) {
    if (isDelivered) return 'done'
    return 'pending'
  }
  return 'pending'
}

const getStepDate = (stepIndex) => {
  if (!order.value) return ''
  const stepState = getStepClass(stepIndex)
  const orderDate = order.value.orderDate
  const shipment = firstShipment.value || {}

  if (stepIndex === 0) {
    if (stepState === 'done') return formatTimelineDate(orderDate) || '—'
    return ''
  }
  if (stepIndex === 1) {
    if (stepState !== 'done') return ''
    if (orderDate) {
      const d = new Date(orderDate)
      if (!isNaN(d.getTime())) {
        d.setTime(d.getTime() + 31 * 60 * 1000)
        return formatTimelineDate(d.toISOString())
      }
    }
    return '—'
  }
  if (stepIndex === 2) {
    if (stepState === 'pending') return ''
    if (shipment.shippedDate || shipment.shippedOn) return formatTimelineDate(shipment.shippedDate || shipment.shippedOn)
    return ''
  }
  if (stepIndex === 3) {
    if (stepState !== 'done') return ''
    if (shipment.deliveredDate || shipment.deliveredOn) return formatTimelineDate(shipment.deliveredDate || shipment.deliveredOn)
    if (shipment.estimatedDeliveryDate || shipment.estimatedArrivalDate) return formatTimelineDate(shipment.estimatedDeliveryDate || shipment.estimatedArrivalDate)
    return ''
  }
  return ''
}

const shipments = computed(() => {
  if (!order.value) return []
  return order.value.shipments || order.value.tracking || []
})

const firstShipment = computed(() => {
  const s = shipments.value
  return s.length > 0 ? s[0] : null
})

const canRequestReturnOrExchange = computed(() => {
  if (!order.value) return false
  const status = String(order.value.orderStatus || order.value.status || '').toLowerCase()
  return ['shipped', 'intransit', 'outfordelivery', 'delivered', 'completed', 'paid', 'processing'].includes(status)
})

const getTrackingUrl = (url) => {
  if (!url) return null
  if (/^https?:\/\//i.test(url)) return url
  return 'https://' + url
}

const buildReturnMailto = () => {
  const orderNum = order.value?.orderNumber || ''
  const subject = encodeURIComponent(`Richiesta cambio / reso - Ordine ${orderNum}`)
  const body = encodeURIComponent(
    `Buongiorno,\nVorrei richiedere un cambio o un reso per l'ordine ${orderNum}.\n\nOrdine: ${orderNum}\nData: ${order.value?.orderDate ? formatDate(order.value.orderDate) : ''}\n\nGrazie.`
  )
  return `mailto:${supportEmail}?subject=${subject}&body=${body}`
}

const fetchOrder = async () => {
  try {
    isLoading.value = true
    error.value = null
    const data = await salesOrderService.getOrderDetails(route.params.id)
    order.value = data
  } catch (err) {
    error.value = 'Impossibile recuperare i dettagli dell\'ordine.'
    console.error(err)
  } finally {
    isLoading.value = false
  }
}

const goToPayment = () => {
  router.push({
    name: 'Payment',
    query: {
      orderId: order.value.salesOrderId,
      total: order.value.orderTotal,
      orderNumber: order.value.orderNumber
    }
  })
}

onMounted(fetchOrder)
</script>

<style scoped>
.order-details-page {
  background: #fcfcfc;
  min-height: 100vh;
}

.back-link {
  color: #000;
  text-decoration: none;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 1.5px;
  transition: opacity 0.2s;
}

.back-link:hover {
  opacity: 0.6;
}

.card-premium {
  background: #fff;
  border: 1px solid #eee;
}

.order-title {
  font-size: 18px;
  font-weight: 900;
  letter-spacing: 1px;
}

.section-title {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: #000;
}

.product-name {
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.product-meta {
  font-size: 10px;
  color: #888;
  letter-spacing: 0.5px;
}

.product-price {
  font-size: 15px;
}

.price-vat-label-details {
  font-size: 9px;
  color: #888;
  font-weight: 400;
  margin-top: 2px;
  letter-spacing: 0.5px;
  text-transform: none;
}

.status-badge {
  font-size: 9px;
  font-weight: 800;
  text-transform: uppercase;
  padding: 6px 12px;
  letter-spacing: 0.8px;
  border-radius: 0;
}

.status-badge.pendingpayment { background: #fff9e6; color: #b08905; }
.status-badge.processing { background: #e0f7fa; color: #006064; }
.status-badge.paymentfailed { background: #fff5f5; color: #c0392b; }
.status-badge.cancelled { background: #f8f9fa; color: #6c757d; }
.status-badge.paid { background: #e6fcf5; color: #087f5b; }
.status-badge.shipped { background: #e3f2fd; color: #1565c0; }
.status-badge.delivered { background: #e8f5e9; color: #2e7d32; }
.status-badge.completed { background: #e8f5e9; color: #2e7d32; }

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ccc;
}
.status-dot.pending { background: #ffc107; }
.status-dot.processing { background: #006064; }
.status-dot.shipped { background: #1565c0; }
.status-dot.intransit { background: #ff6f00; }
.status-dot.outfordelivery { background: #ff6f00; }
.status-dot.delivered { background: #2e7d32; }
.status-dot.cancelled { background: #6c757d; }

.italic { font-style: italic; }
.letter-spacing-1 { letter-spacing: 1.2px; }
.last-child-mb-0:last-child { margin-bottom: 0 !important; }
.last-child-border-0:last-child { border-bottom: none; }
.payment-method-icon { width: 28px; height: 20px; object-fit: contain; background: #1a1a1a; border-radius: 3px; padding: 1px; }

/* Timeline */
.timeline-section {
  padding: 4px 0;
}

.steps {
  display: flex;
  align-items: flex-start;
}

.step {
  flex: 1;
  text-align: center;
  position: relative;
}

.step .dot {
  width: 11px;
  height: 11px;
  border-radius: 50%;
  background: #fff;
  border: 2px solid #e4e4e4;
  margin: 0 auto 10px;
  position: relative;
  z-index: 2;
}

.step.done .dot {
  background: #0d0d0b;
  border-color: #0d0d0b;
}

.step.current .dot {
  background: #fff;
  border: 2px solid #1f7a45;
  box-shadow: 0 0 0 4px rgba(31, 122, 69, 0.25);
}

.step .bar {
  position: absolute;
  top: 5px;
  left: -50%;
  width: 100%;
  height: 2px;
  background: #e4e4e4;
  z-index: 1;
}

.step:first-child .bar {
  display: none;
}

.step.done .bar {
  background: #0d0d0b;
}

.step.current .bar {
  background: #1f7a45;
}

.step .label {
  font-size: 10px;
  letter-spacing: 0.06em;
  color: #7a7a7a;
  font-weight: 600;
}

.step.done .label,
.step.current .label {
  color: #0d0d0b;
}

.step .date {
  font-size: 10px;
  color: #7a7a7a;
  margin-top: 3px;
}

.step.current .date {
  color: #0d0d0b;
  font-weight: 600;
}

/* Help section */
.help-section {
  border: 1px solid #eee;
  border-radius: 0;
}

.help-section .section-title {
  color: #000;
}

.help-section p {
  font-size: 12.5px;
  color: #7a7a7a;
  line-height: 1.6;
}

/* Thumb placeholder for product without image */
.thumb-placeholder-box {
  width: 80px;
  height: 80px;
  background: #111;
  border: 1px solid #eee;
  border-radius: 4px;
}

.thumb-placeholder-text {
  color: #fff;
  font-size: 18px;
  font-weight: 700;
}

/* Responsive */
@media (max-width: 420px) {
  .step .label {
    font-size: 9px;
  }
}
</style>
